import React, { useState, useEffect, useCallback, useRef, Suspense } from 'react';
import { Loader } from '@react-three/drei/web/Loader';
import { Html } from '@react-three/drei/web/Html';
import Model from '../BaseModel/index';
import { useDigitalStore } from '@/store/digital';

/**
 * 动画类型说明：Hold、Show、Touched 所有数字人一定存在，Attract、Chat 不一定存在，需要兼容
 */
type AnimationType = 'Hold' | 'Attract' | 'Show' | 'Touched' | 'Chat';

const BotModel = ({ pageStatus, currentBot }: any) => {
  const isWelcomed = useDigitalStore((state) => state.isWelcomed);
  const machineConfig = {
    modelConfig: {
      "type": "3D",
      "url": "https://newscdn.oss-cn-hangzhou.aliyuncs.com/cultural-data-platform/shuyou/machine/models/ancient_boy_Sushi_noShowEmo.glb",
      "needBs": true,
      "x": 0,
      "y": -0.1,
      "z": 0,
      "scale": 1.3
    }
  };

  const [animationList, setAnimationList] = useState<AnimationType[]>([]);
  const [animation, setAnimation] = useState<AnimationType>('Hold');

  const attractIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const touchedTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const botStatusRef = useRef(currentBot?.status);
  useEffect(() => {
    botStatusRef.current = currentBot?.status;
  }, [currentBot?.status]);

  /**
   * 恢复当前需要循环的动画
   */
  const recoverRepeatAnimation = useCallback(() => {
    // 说话状态下，有 Chat 用 Chat，没有则用 Hold
    if (botStatusRef.current === 'loading') {
      if (animationList?.includes('Chat')) {
        setAnimation('Chat');
      } else {
        setAnimation('Hold');
      }
    } else {
      setAnimation('Hold');
    }
  }, [animationList]);

  // 在首页时，每 10 秒触发一次招揽动作 Attract
  useEffect(() => {
    if (pageStatus === 'Home') {
      if (attractIntervalRef.current) clearInterval(attractIntervalRef.current);
      attractIntervalRef.current = setInterval(() => {
        // 动画在首页时，每 10 秒触发一次
        // attract 或者 show 随机
        const random = Math.random();
        if (random < 0.5) {
          setAnimation('Attract');
        } else {
          setAnimation('Show');
        }
      }, 10000); // 每 10 秒触发一次 Attract
    } else {
      if (attractIntervalRef.current) clearInterval(attractIntervalRef.current);
      recoverRepeatAnimation(); // 其他情况下恢复循环动画
    }

    return () => {
      if (attractIntervalRef.current) clearInterval(attractIntervalRef.current);
    };
  }, [animationList, pageStatus, recoverRepeatAnimation]);

  // 说话时，随机 5 - 10 秒播放 Touched 动画
  useEffect(() => {
    if (currentBot.status === 'loading') {
      if (attractIntervalRef.current) clearInterval(attractIntervalRef.current);

      const randomDelay = Math.floor(Math.random() * 10000) + 5000; // 随机 5 - 15 秒
      if (touchedTimeoutRef.current) clearTimeout(touchedTimeoutRef.current);
      touchedTimeoutRef.current = setTimeout(() => {
        setAnimation('Touched');
      }, randomDelay);
    } else {
      if (touchedTimeoutRef.current) clearTimeout(touchedTimeoutRef.current);
    }

    return () => {
      if (touchedTimeoutRef.current) clearTimeout(touchedTimeoutRef.current);
    };
  }, [currentBot.status]);

  useEffect(() => {
    if (isWelcomed && animationList?.includes('Show')) {
      setAnimation('Show');
    }
  }, [animationList, isWelcomed]);

  if (!machineConfig.modelConfig?.url) {
    return <></>;
  }

  return (
    <group>
      <Suspense
        fallback={
          <Html>
            <Loader dataStyles={{ color: '#202020' }}></Loader>
          </Html>
        }
      >
        <Model
          url={machineConfig.modelConfig.url}
          position={[machineConfig.modelConfig.x, machineConfig.modelConfig.y, machineConfig.modelConfig.z]}
          scale={machineConfig.modelConfig.scale}
          visibleDelay={250}
          animation={animation}
          animationRepeat={animation === 'Hold' || animation === 'Chat'}
          onAnimationFinished={recoverRepeatAnimation}
          onAnimationLoop={recoverRepeatAnimation}
          setAnimationList={setAnimationList}
        />
      </Suspense>
    </group>
  );
};

export default React.memo(BotModel);
